// src/Hooks/useImportExcelReinstate.js

import { db, storage } from '../Firebase/firebase';
import {
  collection,
  writeBatch,
  doc,
  query,
  where,
  getDocs,
  getDoc,
} from 'firebase/firestore';
import { ref, getBlob } from 'firebase/storage';

const normalizeString = (str) => {
  if (str === null || str === undefined) return '';
  return String(str)
    .replace(/[\s,]+/g, '')
    .toLowerCase();
};

const yieldToMain = () => new Promise((resolve) => setTimeout(resolve, 15));

const forcePaint = () =>
  new Promise((resolve) => {
    if (document.hidden) {
      setTimeout(resolve, 0);
    } else {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(resolve, 0);
        });
      });
    }
  });

const buildMappingsFromSnapshot = (snap) => {
  if (!snap) return [];
  const reconstructedMappings = [];

  if (snap.customRows) {
    snap.customRows.forEach((cRow) => {
      reconstructedMappings.push({
        id: cRow.id,
        property: cRow.name,
        assignedTo: snap.rowMappings?.[cRow.id] || '',
        selection: snap.rowSubMappings?.[cRow.id] || '',
        value: snap.rowStaticValues?.[cRow.id] || '',
        isCustom: true,
      });
    });
  }

  Object.keys(snap.rowMappings || {}).forEach((key) => {
    if (!reconstructedMappings.some((m) => m.id === key)) {
      let propName = key.startsWith('prop_') ? key.substring(5) : '-';
      if (key === 'prop_name') propName = 'Name';
      else if (key === 'prop_images') propName = 'Images';
      else if (key === 'prop_richtext') propName = 'Rich Text';
      else if (key.startsWith('prop_color_')) {
        const hex = key.substring(11);
        propName = `Color #${hex}`;
      } else if (propName !== '-')
        propName = propName.charAt(0).toUpperCase() + propName.slice(1);

      reconstructedMappings.push({
        id: key,
        property: propName,
        assignedTo: snap.rowMappings[key] || '',
        selection: snap.rowSubMappings?.[key] || '',
        value: snap.rowStaticValues?.[key] || '',
        isCustom: true,
      });
    }
  });

  return reconstructedMappings;
};

const extractMappingsFromMenu = (
  menu,
  selectedMenuId,
  isMenuDirty,
  stateCustomRows,
  stateRowMappings,
  stateRowSubMappings,
  stateRowStaticValues,
) => {
  if (!menu) return [];

  // If this menu is currently selected and has unsaved edits in state, compare against those live edits
  if (menu.id === selectedMenuId && isMenuDirty) {
    return buildMappingsFromSnapshot({
      customRows: stateCustomRows || [],
      rowMappings: stateRowMappings || {},
      rowSubMappings: stateRowSubMappings || {},
      rowStaticValues: stateRowStaticValues || {},
    });
  }

  if (menu.mappings && Array.isArray(menu.mappings)) {
    return menu.mappings.map((m) => ({
      id: m.id,
      property: m.property || '-',
      assignedTo: m.assignedTo || '',
      selection: m.selection || '',
      value: m.value || '',
      isCustom: true,
    }));
  }

  return buildMappingsFromSnapshot({
    customRows: menu.customRows || [],
    rowMappings: menu.rowMappings || {},
    rowSubMappings: menu.rowSubMappings || {},
    rowStaticValues: menu.rowStaticValues || {},
  });
};

const areMappingsIdentical = (mappingsA, mappingsB) => {
  if (!Array.isArray(mappingsA) || !Array.isArray(mappingsB)) return false;
  if (mappingsA.length !== mappingsB.length) return false;

  for (let i = 0; i < mappingsA.length; i++) {
    const a = mappingsA[i];
    const b = mappingsB[i];

    const propA = String(a.property ?? '-').trim();
    const propB = String(b.property ?? '-').trim();
    const assignA = String(a.assignedTo ?? '').trim();
    const assignB = String(b.assignedTo ?? '').trim();
    const selA = String(a.selection ?? '').trim();
    const selB = String(b.selection ?? '').trim();
    const valA = String(a.value ?? '').trim();
    const valB = String(b.value ?? '').trim();

    if (propA !== propB || assignA !== assignB || selA !== selB || valA !== valB) {
      return false;
    }
  }

  return true;
};

const SHAPE_STYLE_KEYS = [
  'dashColor',
  'isDashed',
  'dashLength',
  'dashGap',
  'strokeColor',
  'strokeWeight',
  'strokeOpacity',
  'fillColor',
  'fillOpacity',
];

export const useImportExcelReinstate = (stateStore) => {
  const { state, setters, refs } = stateStore;

  const executeReinstateBatch = async (skipTemplateConfirm = false) => {
    const {
      selectedHistoryIds,
      sessionBatches,
      categories,
      regions,
      markerIcons,
      customRows: stateCustomRows,
      rowMappings: stateRowMappings,
      rowSubMappings: stateRowSubMappings,
      rowStaticValues: stateRowStaticValues,
      importHistory,
      savedMenus,
      localMenus,
      selectedMenuId,
      isMenuDirty,
    } = state;

    if (selectedHistoryIds.length === 0) return;

    // Check if any selected batch lacks a saved template (no templateSnapshot and no existing menuId)
    const batchesWithoutSavedTemplate = selectedHistoryIds.filter((batchId) => {
      const batchRecord = importHistory?.find((h) => h.id === batchId);
      const hasSnapshot = Boolean(batchRecord?.templateSnapshot);
      const hasValidMenuId =
        Boolean(batchRecord?.menuId) &&
        (savedMenus?.some((m) => m.id === batchRecord.menuId) ||
          localMenus?.some((m) => m.id === batchRecord.menuId));
      return !hasSnapshot && !hasValidMenuId;
    });

    if (batchesWithoutSavedTemplate.length > 0 && !skipTemplateConfirm) {
      if (!selectedMenuId) {
        setters.setConfirmDialog({
          open: true,
          type: 'SELECT_TEMPLATE_FOR_REINSTATE',
          message: 'Select a template before you Reinstate the batch',
          confirmText: 'OK',
          cancelText: 'Cancel',
          hideCancel: false,
          hideConfirm: false,
          severity: 'warning',
          onConfirm: () => {
            setters.setConfirmDialog((prev) => ({
              ...prev,
              open: false,
              type: null,
              onConfirm: null,
            }));
            setters.setViewMode('MENU_VIEW');
          },
        });
        return;
      } else if (refs?.isTemplateFromReinstateRef?.current) {
        setters.setConfirmDialog({
          open: true,
          type: 'CONFIRM_TEMPLATE_FOR_REINSTATE',
          message: 'The selected template will be used',
          confirmText: 'OK',
          cancelText: 'Cancel',
          hideCancel: false,
          hideConfirm: false,
          severity: 'info',
          onConfirm: () => {
            setters.setConfirmDialog((prev) => ({
              ...prev,
              open: false,
              type: null,
              onConfirm: null,
            }));
            executeReinstateBatch(true);
          },
        });
        return;
      }
    }

    setters.setUploadProgress({ current: 0, total: 0 });
    setters.setIsProcessing(true);
    setters.setProgressMessage('Preparing batch data...');

    await yieldToMain();

    try {
      let allRestoredData = [];
      let allStagedFiles = [];
      let finalActiveCols = new Set(state.columns || []);
      let newMenuToCreate = null;
      let totalMarkers = 0;

      // 1. Fetch data for all selected batches to calculate total progress
      const batchesData = [];
      for (const batchId of selectedHistoryIds) {
        const groupsQuery = query(
          collection(db, 'markerGroups'),
          where('importBatchId', '==', batchId),
        );
        const groupsSnapshot = await getDocs(groupsQuery);

        const markersQuery = query(
          collection(db, 'markers'),
          where('importBatchId', '==', batchId),
        );
        const markersSnapshot = await getDocs(markersQuery);

        // Sort documents alphabetically by title before processing
        const sortedDocs = [...groupsSnapshot.docs].sort((a, b) => {
          const titleA = (a.data().title || '').toLowerCase();
          const titleB = (b.data().title || '').toLowerCase();
          return titleA.localeCompare(titleB);
        });

        totalMarkers += sortedDocs.length;
        const batchRecord = importHistory?.find((h) => h.id === batchId);
        batchesData.push({ batchId, batchRecord, sortedDocs, markersSnapshot });
      }

      let currentIndex = 0;

      // 2. Process each batch
      for (const { batchId, batchRecord, sortedDocs, markersSnapshot } of batchesData) {
        const usedMenuId = batchRecord?.menuId || selectedMenuId;
        const existingMenu =
          localMenus?.find((m) => m.id === usedMenuId) ||
          savedMenus?.find((m) => m.id === usedMenuId);
        const menuExists = Boolean(existingMenu);

        let activeCustomRows = stateCustomRows || [];
        let activeRowMappings = stateRowMappings || {};
        let activeRowSubMappings = stateRowSubMappings || {};
        let activeRowStaticValues = stateRowStaticValues || {};

        let finalMenuId = usedMenuId;

        const reconstructedMappings = batchRecord?.templateSnapshot
          ? buildMappingsFromSnapshot(batchRecord.templateSnapshot)
          : [];

        let isSameTemplate = false;
        if (menuExists && batchRecord?.templateSnapshot) {
          const existingMappings = extractMappingsFromMenu(
            existingMenu,
            selectedMenuId,
            isMenuDirty,
            stateCustomRows,
            stateRowMappings,
            stateRowSubMappings,
            stateRowStaticValues,
          );
          isSameTemplate = areMappingsIdentical(reconstructedMappings, existingMappings);
        }

        // Create a new menu if the original menu is missing OR if the template has been modified since import
        if (
          batchRecord?.templateSnapshot &&
          (!menuExists || !isSameTemplate) &&
          !newMenuToCreate
        ) {
          finalMenuId = doc(collection(db, 'excelImportMenus')).id;
          const cleanFilename = batchRecord.filename
            ? batchRecord.filename.replace(/\.[^/.]+$/, '')
            : existingMenu?.title || 'Unknown';

          const createdMenu = {
            id: finalMenuId,
            title: `${cleanFilename} (Reinstated)`,
            updatedAt: new Date().toISOString(),
            mappings: reconstructedMappings,
          };

          newMenuToCreate = createdMenu;
          setters.setLocalMenus((prev) => [createdMenu, ...prev]);
          setters.setIsMenuDirty(false);
        } else if (newMenuToCreate && (!menuExists || !isSameTemplate)) {
          finalMenuId = newMenuToCreate.id;
        }

        // Apply mappings for this specific batch
        if (batchRecord?.templateSnapshot) {
          activeCustomRows = batchRecord.templateSnapshot.customRows
            ? [...batchRecord.templateSnapshot.customRows]
            : [];
          activeRowMappings = batchRecord.templateSnapshot.rowMappings || {};
          activeRowSubMappings = batchRecord.templateSnapshot.rowSubMappings || {};
          activeRowStaticValues = batchRecord.templateSnapshot.rowStaticValues || {};

          // Convert KML intrinsic mapped fields into explicit customRows for Excel
          Object.keys(activeRowMappings).forEach((key) => {
            if (
              key.startsWith('prop_') &&
              !activeCustomRows.some((cr) => cr.id === key)
            ) {
              let propName = key.substring(5);
              if (key === 'prop_name') propName = 'Name';
              else if (key === 'prop_images') propName = 'Images';
              else if (key === 'prop_richtext') propName = 'Rich Text';
              else if (key.startsWith('prop_color_')) {
                const hex = key.substring(11);
                propName = `Color #${hex}`;
              } else propName = propName.charAt(0).toUpperCase() + propName.slice(1);

              activeCustomRows.push({
                id: key,
                name: propName,
                isCustom: true,
              });
            }
          });

          // Only update global setters if it's the LAST or ONLY batch to ensure the UI matches
          if (
            selectedHistoryIds.length === 1 ||
            batchId === selectedHistoryIds[selectedHistoryIds.length - 1]
          ) {
            setters.setCustomRows(activeCustomRows);
            setters.setRowMappings(activeRowMappings);
            setters.setRowSubMappings(activeRowSubMappings);
            setters.setRowStaticValues(activeRowStaticValues);
            if (finalMenuId) setters.setSelectedMenuId(finalMenuId);
          }
        } else if (finalMenuId && !newMenuToCreate) {
          const menuToRestore =
            savedMenus?.find((m) => m.id === finalMenuId) ||
            localMenus?.find((m) => m.id === finalMenuId);

          if (menuToRestore) {
            if (menuToRestore.mappings && menuToRestore.mappings.length > 0) {
              activeCustomRows = [];
              activeRowMappings = {};
              activeRowSubMappings = {};
              activeRowStaticValues = {};

              menuToRestore.mappings.forEach((mapping) => {
                activeCustomRows.push({ id: mapping.id, name: mapping.property });
                if (mapping.assignedTo)
                  activeRowMappings[mapping.id] = mapping.assignedTo;
                if (mapping.selection)
                  activeRowSubMappings[mapping.id] = mapping.selection;
                if (mapping.value) activeRowStaticValues[mapping.id] = mapping.value;
              });
            } else if (menuToRestore.customRows) {
              activeCustomRows = menuToRestore.customRows || [];
              activeRowMappings = menuToRestore.rowMappings || {};
              activeRowSubMappings = menuToRestore.rowSubMappings || {};
              activeRowStaticValues = menuToRestore.rowStaticValues || {};
            }

            if (
              selectedHistoryIds.length === 1 ||
              batchId === selectedHistoryIds[selectedHistoryIds.length - 1]
            ) {
              setters.setCustomRows(activeCustomRows);
              setters.setRowMappings(activeRowMappings);
              setters.setRowSubMappings(activeRowSubMappings);
              setters.setRowStaticValues(activeRowStaticValues);
              setters.setSelectedMenuId(finalMenuId);
            }
          }
        }

        const templateCols = activeCustomRows.filter(
          (r) => activeRowMappings[r.id] && r.name !== '-',
        );
        let activeCols = templateCols.map((r) => r.name);

        const useFallback = activeCols.length === 0;
        if (useFallback) {
          activeCols = [
            'Title',
            'POSITION (LAT, LNG)',
            'Category',
            'Region',
            'Address',
            'City',
            'State',
            'Zip',
            'Country',
            'Description',
            'Hover Text',
            'Visibility',
            'Date',
            'Client Name',
            'Controller',
            'Keywords',
            'Web Site',
            'Showcase',
            'Hilight',
            'Photos',
            'Hover Photo',
            'Documents',
            'Icon',
          ];
        }

        activeCols.forEach((c) => finalActiveCols.add(c));

        const getColNameByMapping = (target, subTarget = null) => {
          const key = Object.keys(activeRowMappings).find((k) => {
            if (normalizeString(activeRowMappings[k]) !== normalizeString(target))
              return false;
            if (
              subTarget &&
              normalizeString(activeRowSubMappings[k]) !== normalizeString(subTarget)
            )
              return false;
            return true;
          });
          if (key) {
            const cr = activeCustomRows.find((r) => r.id === key);
            return cr && cr.name !== '-'
              ? cr.name
              : key.startsWith('prop_')
                ? key.substring(5)
                : null;
          }
          return null;
        };

        // Process markers
        for (let idx = 0; idx < sortedDocs.length; idx++) {
          setters.setProgressMessage(
            `Reinstating ${currentIndex + 1} / ${totalMarkers} markers...`,
          );
          setters.setUploadProgress({ current: currentIndex + 1, total: totalMarkers });
          await forcePaint();
          currentIndex++;

          const gDoc = sortedDocs[idx];
          const gData = gDoc.data();
          let mDoc = markersSnapshot.docs.find(
            (m) => m.data().markerGroupID === gDoc.id || m.id === gData.markerID,
          );
          let mData = mDoc ? mDoc.data() : null;

          if (!mData && gData.markerID) {
            try {
              const directMarkerSnap = await getDoc(doc(db, 'markers', gData.markerID));
              if (directMarkerSnap.exists()) {
                mData = directMarkerSnap.data();
              }
            } catch (err) {
              console.warn('Fallback marker fetch failed during reinstate:', err);
            }
          }

          const catName = categories.find((c) => c.id === gData.categoryId)?.name || '';
          const regName = regions.find((r) => r.id === gData.regionId)?.name || '';

          const matchedFiles = [];
          if (gData.photos && Array.isArray(gData.photos)) {
            for (const p of gData.photos) {
              if (
                (p.type === 'image' || p.type === 'video' || p.type === 'audio') &&
                p.url
              ) {
                let blobToUse = null;
                let objectUrl = '';
                try {
                  const fileRef = ref(storage, p.url);
                  blobToUse = await getBlob(fileRef);
                  objectUrl = URL.createObjectURL(blobToUse);
                } catch (storageErr) {
                  try {
                    const res = await fetch(p.url);
                    if (!res.ok)
                      throw new Error('Fetch failed with status ' + res.status);
                    const blob = await res.blob();
                    blobToUse = blob;
                    objectUrl = URL.createObjectURL(blob);
                  } catch (e) {
                    console.warn(
                      'Failed to fetch media for reinstate, using url fallback',
                      e,
                    );
                  }
                }

                let file;
                if (blobToUse) {
                  file = new File(
                    [blobToUse],
                    p.fileName ||
                      `restored_${p.id}.${p.type === 'video' ? 'mp4' : p.type === 'audio' ? 'mp3' : 'jpg'}`,
                    { type: blobToUse.type || p.type },
                  );
                  file.preview = objectUrl;
                } else {
                  file = {
                    name:
                      p.fileName ||
                      `restored_${p.id}.${p.type === 'video' ? 'mp4' : p.type === 'audio' ? 'mp3' : 'jpg'}`,
                    type: p.type,
                    url: p.url,
                    preview: p.url,
                    isRestoredRemoteFile: true,
                  };
                }

                if (p.description) file.description = p.description;
                matchedFiles.push(file);
                allStagedFiles.push(file);
              }
            }
          }

          const restoredDocuments = [];
          if (gData.documents && Array.isArray(gData.documents)) {
            for (const d of gData.documents) {
              if (d.url) {
                let blobToUse = null;
                let objectUrl = '';
                try {
                  const fileRef = ref(storage, d.url);
                  blobToUse = await getBlob(fileRef);
                  objectUrl = URL.createObjectURL(blobToUse);
                } catch (e) {
                  try {
                    const res = await fetch(d.url);
                    if (res.ok) {
                      blobToUse = await res.blob();
                      objectUrl = URL.createObjectURL(blobToUse);
                    }
                  } catch (err) {
                    console.warn(
                      'Failed to fetch document for reinstate via fallback',
                      err,
                    );
                  }
                }

                let file;
                if (blobToUse) {
                  file = new File(
                    [blobToUse],
                    d.fileName || d.name || `restored_doc_${d.id || Date.now()}`,
                    { type: blobToUse.type || 'application/octet-stream' },
                  );
                  file.preview = objectUrl;
                } else {
                  file = {
                    name: d.fileName || d.name || `restored_doc_${d.id || Date.now()}`,
                    url: d.url,
                    preview: d.url,
                    isRestoredRemoteFile: true,
                  };
                }
                restoredDocuments.push(file);
              }
            }
          }

          let hoverPhotoName = gData.hoverImage || '';
          let restoredHoverFile = null;

          if (gData.hoverImage) {
            let hoverBlob = null;
            let hoverObjUrl = '';
            let extractedHoverName = `restored_hover_${gDoc.id}.jpg`;

            try {
              const decodedUrl = decodeURIComponent(gData.hoverImage.split('?')[0]);
              const lastSegment = decodedUrl.substring(decodedUrl.lastIndexOf('/') + 1);
              if (lastSegment) {
                extractedHoverName = lastSegment.replace(/^(?:\d+_|hover_\d+_)/, '');
              }
            } catch (err) {}

            try {
              const hoverRef = ref(storage, gData.hoverImage);
              hoverBlob = await getBlob(hoverRef);
              hoverObjUrl = URL.createObjectURL(hoverBlob);
            } catch (e) {
              try {
                const res = await fetch(gData.hoverImage);
                if (res.ok) {
                  hoverBlob = await res.blob();
                  hoverObjUrl = URL.createObjectURL(hoverBlob);
                }
              } catch (err) {
                console.warn('Failed to fetch hoverImage for reinstate:', err);
              }
            }

            if (hoverBlob) {
              restoredHoverFile = new File([hoverBlob], extractedHoverName, {
                type: hoverBlob.type || 'image/jpeg',
              });
              restoredHoverFile.preview = hoverObjUrl;
              hoverPhotoName = extractedHoverName;
              allStagedFiles.push(restoredHoverFile);
            }
          }

          let descriptionText = '';
          if (gData.photos && Array.isArray(gData.photos)) {
            const textPhoto = gData.photos.find((p) => p.type === 'text');
            if (textPhoto) {
              descriptionText = textPhoto.description;
            } else {
              const firstImg = gData.photos.find(
                (p) =>
                  (p.type === 'image' || p.type === 'video' || p.type === 'audio') &&
                  p.description,
              );
              if (firstImg) descriptionText = firstImg.description;
            }
          }

          const row = {
            id: `restored_${Date.now()}_${batchId}_${idx}`,
            photoMatch: matchedFiles.length > 0 ? 'MATCH' : 'NO_MATCH',
            matchedFiles: matchedFiles,
            _manuallyAssigned: {},
            _localMappings: {},
            documents: restoredDocuments,
            originalMarkerGroupID: gDoc.id,
          };

          if (restoredHoverFile) {
            row._restoredHoverFile = restoredHoverFile;
          }
          if (gData.hoverImage) {
            row._restoredHoverUrl = gData.hoverImage;
          }
          if (gData.polygonID) {
            row.polygonID = gData.polygonID;
          }
          if (gData.polylineID) {
            row.polylineID = gData.polylineID;
          }

          // Preserve polygon/polyline style properties internally without exposing them as table columns
          const shapeStyleProps = {};
          SHAPE_STYLE_KEYS.forEach((sKey) => {
            if (gData[sKey] !== undefined) {
              shapeStyleProps[sKey] = gData[sKey];
            }
          });
          if (Object.keys(shapeStyleProps).length > 0) {
            row._shapeStyleProps = shapeStyleProps;
          }

          if (mData?.position?.lat !== undefined && mData?.position?.lng !== undefined) {
            row.lat = mData.position.lat;
            row.lng = mData.position.lng;
          }

          // Initialize all cols for this batch
          activeCols.forEach((c) => (row[c] = ''));

          const successfullyMappedGDataKeys = new Set();
          const successfullyAssignedCols = new Set();

          const fbMap = {
            'INFO|TITLE': 'Title',
            'POSITION (LAT, LNG)|null': 'POSITION (LAT, LNG)',
            'INFO|CATEGORY': 'Category',
            'INFO|REGION': 'Region',
            'INFO|LOCATION ADDRESS': 'Address',
            'INFO|CITY': 'City',
            'INFO|STATE': 'State',
            'INFO|POSTCODE/ZIP': 'Zip',
            'INFO|COUNTRY': 'Country',
            'INFO|CLIENT NAME': 'Client Name',
            'INFO|CONTROLLER': 'Controller',
            'INFO|KEYWORDS': 'Keywords',
            'INFO|WEB SITE': 'Web Site',
            'INFO|DATE': 'Date',
            'INFO|SHOWCASE': 'Showcase',
            'INFO|HILIGHT': 'Hilight',
            'VISIBILITY|null': 'Visibility',
            'HOVER TEXT|null': 'Hover Text',
            'DESCRIPTION|null': 'Description',
            'HOVER PHOTO|null': 'Hover Photo',
            'PHOTOS|null': 'Photos',
            'DOCUMENTS|null': 'Documents',
            'ICON|null': 'Icon',
          };

          const setMappedValue = (
            target,
            subTarget,
            value,
            gDataKey = null,
            markAsManual = false,
          ) => {
            if (value === undefined || value === null || value === '') return;

            let colName = getColNameByMapping(target, subTarget);
            const key = `${target}|${subTarget}`;
            const fallbackName = fbMap[key];

            // If a template shares the same Property Name for two different targets (e.g. Title and Hover Text),
            // ALWAYS decouple them upon reinstate to ensure the data is explicitly visible in its own column.
            if (
              colName &&
              successfullyAssignedCols.has(colName.toLowerCase()) &&
              fallbackName
            ) {
              const actualCol = activeCols.find(
                (c) =>
                  c.toLowerCase() === fallbackName.toLowerCase() &&
                  !successfullyAssignedCols.has(c.toLowerCase()),
              );
              colName = actualCol || fallbackName;
            }

            if (!colName && fallbackName) {
              const actualCol = activeCols.find(
                (c) => c.toLowerCase() === fallbackName.toLowerCase(),
              );
              colName = actualCol || fallbackName;
            }

            if (colName) {
              row[colName] = value;
              finalActiveCols.add(colName);
              if (markAsManual) {
                if (target === 'POSITION (LAT, LNG)') {
                  row._manuallyAssigned['coords'] = true;
                } else {
                  row._manuallyAssigned[colName] = true;
                }
              }
              successfullyAssignedCols.add(colName.toLowerCase());
              if (gDataKey) successfullyMappedGDataKeys.add(gDataKey.toLowerCase());
            }
          };

          setMappedValue('INFO', 'TITLE', gData.title, 'title');
          setMappedValue(
            'POSITION (LAT, LNG)',
            null,
            mData?.position?.lat && mData?.position?.lng
              ? `${mData.position.lat}, ${mData.position.lng}`
              : '',
            'position',
            false,
          );
          setMappedValue('INFO', 'CATEGORY', catName, 'categoryId');
          setMappedValue('INFO', 'REGION', regName, 'regionId');
          setMappedValue('INFO', 'LOCATION ADDRESS', gData.address, 'address');
          setMappedValue('INFO', 'CITY', gData.city, 'city');
          setMappedValue('INFO', 'STATE', gData.state, 'state');
          setMappedValue('INFO', 'POSTCODE/ZIP', gData.zip, 'zip');
          setMappedValue('INFO', 'COUNTRY', gData.country, 'country');
          setMappedValue('INFO', 'CLIENT NAME', gData.clientName, 'clientName');
          setMappedValue('INFO', 'CONTROLLER', gData.controller, 'controller');
          setMappedValue('INFO', 'KEYWORDS', gData.keywords, 'keywords');
          setMappedValue('INFO', 'WEB SITE', gData.website, 'website');
          setMappedValue('INFO', 'DATE', gData.date, 'date');
          setMappedValue('INFO', 'SHOWCASE', gData.showcase, 'showcase');
          setMappedValue('INFO', 'HILIGHT', gData.hilight, 'hilight');
          setMappedValue('VISIBILITY', null, gData.visibility, 'visibility');
          setMappedValue('HOVER TEXT', null, gData.hoverContent, 'hoverContent');
          setMappedValue('DESCRIPTION', null, descriptionText, null);
          setMappedValue('HOVER PHOTO', null, hoverPhotoName, 'hoverImage');

          let restoredIconValue = mData?.iconID;
          if (restoredIconValue && markerIcons) {
            const matchedIcon = markerIcons.find(
              (i) =>
                i.id === restoredIconValue ||
                normalizeString(i.name) === normalizeString(restoredIconValue),
            );
            if (matchedIcon) restoredIconValue = matchedIcon.name;
          }
          setMappedValue('ICON', null, restoredIconValue, 'iconID', false);

          if (matchedFiles.length > 0) {
            setMappedValue(
              'PHOTOS',
              null,
              matchedFiles.length === 1
                ? matchedFiles[0].name
                : `${matchedFiles.length} media files`,
              'photos',
            );
          }

          if (restoredDocuments.length > 0) {
            setMappedValue(
              'DOCUMENTS',
              null,
              restoredDocuments.length === 1
                ? restoredDocuments[0].name
                : `${restoredDocuments.length} documents`,
              'documents',
            );
          }

          const systemKeys = [
            'importBatchId',
            'lastModified',
            'markerID',
            'markerGroupID',
            'polygonID',
            'polylineID',
            'photos',
            'hoverImage',
            'iconID',
            'position',
            'documents',
            ...SHAPE_STYLE_KEYS,
          ].map((k) => k.toLowerCase());

          if (gData) {
            Object.keys(gData).forEach((key) => {
              const lowerKey = key.toLowerCase();
              if (
                !systemKeys.includes(lowerKey) &&
                !successfullyMappedGDataKeys.has(lowerKey)
              ) {
                if (
                  !successfullyAssignedCols.has(lowerKey) &&
                  gData[key] !== null &&
                  gData[key] !== undefined &&
                  gData[key] !== ''
                ) {
                  const actualGridCol = activeCols.find(
                    (c) => c.toLowerCase() === lowerKey,
                  );
                  const finalKey = actualGridCol || key;
                  row[finalKey] = gData[key];
                  finalActiveCols.add(finalKey);
                }
              }
            });
          }
          allRestoredData.push(row);
        }
      }

      // --- Post Loop Consolidation ---
      setters.setPendingReinstateBatchIds((prev) => {
        const newIds = new Set([...(prev || []), ...selectedHistoryIds]);
        return Array.from(newIds);
      });

      if (newMenuToCreate) {
        const createBatch = writeBatch(db);
        const { id, ...menuDataToSave } = newMenuToCreate;
        createBatch.set(doc(db, 'excelImportMenus', id), menuDataToSave);
        await createBatch.commit();
      }

      const reinstatedSessionIds = new Set();
      for (const batchId of selectedHistoryIds) {
        if (sessionBatches[batchId]) {
          sessionBatches[batchId].forEach((id) => reinstatedSessionIds.add(id));
        }
      }

      setters.setParsedData((prev) => {
        const untouchedRows = prev.filter((r) => !reinstatedSessionIds.has(r.id));
        return [...untouchedRows, ...allRestoredData];
      });

      setters.setSuccessfullyImportedIds((prev) =>
        prev.filter((id) => !reinstatedSessionIds.has(id)),
      );
      setters.setSelectedRowIds((prev) =>
        prev.filter((id) => !reinstatedSessionIds.has(id)),
      );

      setters.setStagedFiles((prev) => {
        const existingNames = new Set(prev.map((f) => f.name));
        const newFiles = allStagedFiles.filter((f) => !existingNames.has(f.name));
        return [...prev, ...newFiles];
      });

      setters.setColumns(Array.from(finalActiveCols));

      // Use the filename of the most recently imported batch for context
      const lastBatchRecord = importHistory?.find((h) => h.id === selectedHistoryIds[0]);
      if (lastBatchRecord?.filename) {
        setters.setCurrentFilename(lastBatchRecord.filename);
      }

      if (refs?.isTemplateFromReinstateRef) {
        refs.isTemplateFromReinstateRef.current = true;
      }

      setters.setIsPhotoFlowActive(false);
      setters.setPendingMissingColsCheck(true);
      setters.setSelectedHistoryIds([]);
      setters.setViewMode('DATA_VIEW');

      setters.setAlertState({
        open: true,
        message: `Successfully reinstated ${totalMarkers} markers to the grid.`,
        severity: 'success',
      });
    } catch (error) {
      console.error('Error reinstating batch:', error);
      setters.setAlertState({
        open: true,
        message: `Failed to reinstate import: ${error.message}`,
        severity: 'error',
      });
    } finally {
      setters.setIsProcessing(false);
      setters.setProgressMessage('');
    }
  };

  return { executeReinstateBatch };
};
